import * as React from 'react';
import { FeatureLoader, useAppState } from 'oasis-os-react';
import { Route } from 'react-router';
import { useHistory, Redirect, useLocation } from 'react-router-dom';
import { useField, useNestedApps } from 'oasis-os-contentful';
import { isCookieEnabled, redirectURL } from 'oasis-os-utils';
import { readGlobal, getLogger, updateGlobal } from 'oasis-os-common';
import { isPlatform } from '@ionic/core';
import PrivateRoute from '../PrivateRoute';
import Login from '../Login';
import {
  cookieMngConfig,
  cookieErrorConfig,
  DefaultView,
  RouteObject,
  smartRedirectConfig,
  OnBoarding,
  Routing,
} from '../../types';
import SmartRedirectModal from '../SmartRedirectModal';
import { isSmartRedirect } from '../../utils/getSmartRedirect';

const logger = getLogger(PKG_NAME, PKG_VERSION, 'Routes');
export default function Routes() {
  const publicRefs = useNestedApps('publicView');
  const privateRefs = useNestedApps('view');
  const moduleRefs = useNestedApps('moduleView');
  const defaultViewRef = useField<DefaultView>('defaultView');
  const [, , appInstance] = useAppState();
  const history = useHistory();
  const location = useLocation();
  const [isContinue, setIsContinue] = React.useState<boolean>(false);

  const cookieMng = readGlobal('$.global.value.functionalConfig.cookieMng') as cookieMngConfig;
  const cookieError = readGlobal(
    '$.global.value.functionalConfig.cookieError',
  ) as cookieErrorConfig;
  const smartRedirectFC = readGlobal(
    '$.global.value.functionalConfig.features.smartRedirect',
  ) as smartRedirectConfig;
  const onBoarding: OnBoarding = readGlobal('$.global.value.functionalConfig.onboarding');
  const hydrateEnabled: boolean = readGlobal(
    `$.global.value.functionalConfig.features.hydrate.enabled`,
  );
  const standalone = 'standalone' in window.navigator && window.navigator.standalone;

  const onContinue = () => {
    setIsContinue(true);
  };
  const continueWithWeb = () => {
    const smartRedirectURL = readGlobal(
      '$.global.value.service.smartRedirect',
    );
    if (isPlatform('ios') && smartRedirectURL?.ios) {
      redirectURL(smartRedirectURL?.ios);
    }
    else if (isPlatform('android') && smartRedirectURL?.android) {
      redirectURL(smartRedirectURL?.android);
    }
    else {
      return <Login />;
    }
  };
  // tracks and stores last route into global store according to config and ignoreList
  React.useEffect(() => {
    const routing: Routing = readGlobal('$.global.value.functionalConfig.routing');
    if (routing && location?.pathname) {
      const ignoredRoute = routing.ignoreList.some((rx) => new RegExp(rx).exec(location.pathname));
      routing.storeLastRoute &&
        !ignoredRoute &&
        updateGlobal('routing', { lastRoute: location?.pathname });
    }
  }, [location]);

  React.useState(() => {
    appInstance.registerAction('nav.push', (route: string | RouteObject) => {
      let isOnBoarded = false;
      if (sessionStorage.getItem('onboarded') === 'true') {
        isOnBoarded = true;
      }
      if ((onBoarding?.isEnabled && isOnBoarded) || !onBoarding?.isEnabled) {
        if (typeof route === 'object') {
          history.push(route.path, route.data);
        } else {
          history.push(route);
        }
      }
    });
  });

  return (
    <>
      <Route path="/session">
        {!isContinue &&
        !standalone &&
        smartRedirectFC?.enabled &&
        isPlatform('mobile') &&
        !isSmartRedirect() ? (
          <SmartRedirectModal onContinue={onContinue} />
        ) : (
          continueWithWeb()
        )}
      </Route>
      {(publicRefs || []).map(({ name, meta, entry, ...rest }) => {
        const sharedModule =
          meta?.sharedModules &&
          Array.isArray(meta?.sharedModules) &&
          meta?.sharedModules.length > 0
            ? meta?.sharedModules[0]
            : null;
        return (
          <Route
            key={`${name}-route-${meta.contentId}`}
            path={meta?.route ?? '/'}
            render={() => (
              <FeatureLoader
                key={`${name}-feature-${meta.contentId}`}
                name={name}
                app={{ name }}
                id={meta.contentId}
                initial={entry}
                {...(sharedModule && { sharedModule })}
                hydrate={hydrateEnabled}
                {...rest}
                {...meta}
              />
            )}
          />
        );
      })}
      {(privateRefs || []).map(({ name, meta, entry, ...rest }) => {
        const sharedModule =
          meta?.sharedModules &&
          Array.isArray(meta?.sharedModules) &&
          meta?.sharedModules.length > 0
            ? meta?.sharedModules[0]
            : null;

        return !isPlatform('hybrid') ? (
          <PrivateRoute key={`${name}-private-route-${meta.contentId}`} path={meta?.route ?? '/'}>
            <FeatureLoader
              key={`${name}-private-feature-${meta.contentId}-${sharedModule}`}
              name={name}
              app={{ name }}
              id={meta.contentId}
              sharedModule={meta.sharedModules ? meta.sharedModules[0] : undefined}
              initial={entry}
              hydrate={hydrateEnabled}
              {...(sharedModule && { sharedModule })}
              {...rest}
              {...meta}
            />
          </PrivateRoute>
        ) : (
          <Route
            key={`${name}-route-${meta.contentId}`}
            path={meta?.route ?? '/'}
            render={() => (
              <FeatureLoader
                key={`${name}-feature-${meta.contentId}`}
                name={name}
                app={{ name }}
                id={meta.contentId}
                initial={entry}
                {...rest}
                {...meta}
              />
            )}
          />
        );
      })}
      {(moduleRefs || []).map(({ name, meta, entry, ...rest }) => {
        return (
          <PrivateRoute key={`${name}-private-route-${meta.contentId}`} path={meta?.route ?? '/'}>
            <FeatureLoader
              key={`${name}-private-feature-${meta.contentId}`}
              name={name}
              app={{ name }}
              id={meta.contentId}
              hydrate={hydrateEnabled}
              initial={entry}
              {...rest}
              {...meta}
            />
          </PrivateRoute>
        );
      })}
      <Route exact path="/">
        <Redirect to={`${defaultViewRef?.fields?.route ?? '/home'}`} />
      </Route>
    </>
  );
}
